import {StockFactsDto, mapStockFacts} from '../shared/components/sm-stock-summary/stock-summary.model';
import {HttpClient} from '@angular/common/http';
import {DestroyRef, Injectable, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {Actions, ofType} from '@ngrx/effects';
import {TranslateService} from '@ngx-translate/core';
import {ToastController} from '@ionic/angular';
import {Subject, distinctUntilChanged, firstValueFrom, map} from 'rxjs';
import {AuthService} from '../auth/auth.service';
import {ApiResponse} from '../auth/auth.models';
import {buildApiUrl} from '../shared/api/api-url';
import {SmContextMenuItemModel} from '../shared/components/sm-context-menu-item/sm-context-menu-item.component';
import {buildRecentChangePathText} from '../shared/text/recent-change-path';
import {ReportsActions} from '../reports/store/reports.actions';
import {FavoritesAnalyticsService} from './favorites-analytics.service';

export type FavoriteType = 'container' | 'item';
export type FavoriteSource = 'containers' | 'details' | 'widget' | 'list' | 'reports';
export type FavoriteChange = {entityType: FavoriteType; id: string; isFavorite: boolean};
export type FavoriteRefresh = void | ReturnType<typeof ReportsActions.inventoryMutationCompleted>;
export type FavoriteEntryDto = StockFactsDto & {
  id: string; entityType: FavoriteType; name: string; description: string | null; imageUrl: string | null;
  favoritedAtUtc?: string; canEdit?: boolean; containerId: string; quantity: number | null; path: {id: string; name: string}[];
};
export type FavoritePageDto = {entries: FavoriteEntryDto[]; totalCount: number; nextCursor: string | null};
export type FavoriteEntry = FavoriteEntryDto & {pathText: string};
export type FavoritePage = {entries: FavoriteEntry[]; totalCount: number; nextCursor: string | null};
export type FavoritePreview = {containers: FavoritePage; items: FavoritePage};

function toPage(dto: FavoritePageDto): FavoritePage {
  return {totalCount: dto.totalCount, nextCursor: dto.nextCursor, entries: dto.entries.map(x => ({
    id: x.id, entityType: x.entityType, name: x.name, description: x.description, imageUrl: x.imageUrl,
    favoritedAtUtc: x.favoritedAtUtc, containerId: x.containerId, ...mapStockFacts(x), quantity: x.quantity, canEdit: x.canEdit === true,
    path: x.path.map(segment => ({id: segment.id, name: segment.name})),
    pathText: buildRecentChangePathText({entityType: x.entityType === 'container' ? 'Container' : 'Item', path: x.path, containerLabel: null, locationLabel: null}),
  }))};
}

@Injectable({providedIn: 'root'})
export class FavoritesService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly auth = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly toasts = inject(ToastController);
  private readonly destroyRef = inject(DestroyRef);
  private readonly actions = inject(Actions);
  private readonly analytics = inject(FavoritesAnalyticsService);
  private readonly pending = signal(new Set<string>());
  private generation = 0;
  readonly changes = new Subject<FavoriteChange>();
  readonly refresh = new Subject<FavoriteRefresh>();
  readonly accountChanged = new Subject<void>();

  constructor() {
    this.auth.session$.pipe(map(session => session?.user.id ?? null), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(userId => {
        this.generation++;
        this.pending.set(new Set());
        this.accountChanged.next();
        this.store.dispatch(ReportsActions.accountChanged());
        if (userId) this.store.dispatch(ReportsActions.refreshRequested());
      });
    this.actions.pipe(ofType(ReportsActions.applicationResumed, ReportsActions.inventoryMutationCompleted),
      takeUntilDestroyed(this.destroyRef)).subscribe(action => this.refresh.next(
        action.type === ReportsActions.inventoryMutationCompleted.type ? action : undefined,
      ));
  }

  menu(entityType: FavoriteType, id: string, isFavorite: boolean | undefined): SmContextMenuItemModel {
    return {id: 'favorite', textKey: isFavorite ? 'favorites.remove' : 'favorites.add',
      iconName: isFavorite ? 'star' : 'star-outline', disabled: isFavorite === undefined || this.pending().has(`${entityType}:${id}`)};
  }

  async set(entityType: FavoriteType, id: string, isFavorite: boolean, source: FavoriteSource, allowUndo = true): Promise<void> {
    const key = `${entityType}:${id}`;
    if (this.pending().has(key) || !this.auth.session) return;
    const generation = this.generation;
    this.pending.update(values => new Set([...values, key]));
    try {
      const url = buildApiUrl(`/v1/favorites/${entityType}/${encodeURIComponent(id)}`);
      const response = await firstValueFrom(isFavorite
        ? this.http.put<ApiResponse<{isFavorite: boolean}>>(url, {})
        : this.http.delete<ApiResponse<{isFavorite: boolean}>>(url));
      if (generation !== this.generation) return;
      if (!response.success || !response.data) throw new Error('Favorite update failed');
      this.changes.next({entityType, id, isFavorite: response.data.isFavorite});
      this.store.dispatch(ReportsActions.refreshRequested());
      this.analytics.record(isFavorite ? 'favorite_added' : 'favorite_removed', {entityType, source, outcome: 'succeeded'});
      await this.presentToast({
        message: this.translate.instant(isFavorite ? 'favorites.added' : 'favorites.removed'), duration: 5000,
        buttons: !isFavorite && allowUndo ? [{text: this.translate.instant('favorites.undo'), handler: () => {
          if (generation === this.generation) void this.set(entityType, id, true, source, false);
        }}] : [],
      }, generation);
    } catch {
      if (generation === this.generation) {
        this.analytics.record(isFavorite ? 'favorite_added' : 'favorite_removed', {entityType, source, outcome: 'failed'});
        await this.presentToast({message: this.translate.instant('favorites.updateError'), duration: 5000, htmlAttributes: {role: 'alert'}}, generation);
      }
    } finally {
      if (generation === this.generation) this.pending.update(values => {const next = new Set(values); next.delete(key); return next;});
    }
  }

  private async presentToast(options: Parameters<ToastController['create']>[0], generation: number): Promise<void> {
    try {
      const toast = await this.toasts.create(options);
      if (generation === this.generation) await toast.present();
    } catch {
      // Notification failures must not turn a confirmed server update into a failed action.
    }
  }

  async preview(): Promise<FavoritePreview> {
    const generation = this.generation;
    const response = await firstValueFrom(this.http.get<ApiResponse<{containers: FavoritePageDto; items: FavoritePageDto}>>(buildApiUrl('/v1/favorites/preview')));
    if (generation !== this.generation || !response.success || !response.data) throw new Error('Favorites unavailable');
    return {containers: toPage(response.data.containers), items: toPage(response.data.items)};
  }

  async page(entityType: FavoriteType, cursor?: string | null): Promise<FavoritePage> {
    const generation = this.generation;
    const params: Record<string, string> = {entityType, limit: '20'};
    if (cursor) params['cursor'] = cursor;
    const response = await firstValueFrom(this.http.get<ApiResponse<FavoritePageDto>>(buildApiUrl('/v1/favorites'), {params}));
    if (generation !== this.generation || !response.success || !response.data) throw new Error('Favorites unavailable');
    return toPage(response.data);
  }
}
